import cn from 'classnames';
import { FC } from 'react';
import { FormattedMessage } from 'react-intl';

import { useMedia } from '@/shared/hooks';
import { Icon } from '@/shared/ui';

import { dentalPhotoToolbarMessages } from '../../config';
import type { EditableToolWithControls } from '../../config';
import { RotationSlider } from '../RotationSlider/RotationSlider';

import styles from './EditingBottomBar.module.scss';

type EditingBottomBarProps = {
  className: string;
  activeTool: EditableToolWithControls;
  rotation: number;
  onDone: () => void;
  onCancel: () => void;
  onResetToOriginal: () => void;
  onRotateLeft: () => void;
  onRotateRight: () => void;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onFreeRotation: (angle: number) => void;
};

export const EditingBottomBar: FC<EditingBottomBarProps> = (props) => {
  const {
    className,
    activeTool,
    rotation,
    onDone,
    onCancel,
    onResetToOriginal,
    onRotateLeft,
    onRotateRight,
    onZoomIn,
    onZoomOut,
    onFreeRotation,
  } = props;

  const { isMobile } = useMedia();

  const actionButtons = (
    <div className={styles.actionButtons}>
      <button
        type="button"
        className={styles.cancelButton}
        data-component="cancel-photo-edit"
        onClick={onCancel}
      >
        <FormattedMessage {...dentalPhotoToolbarMessages.cancel} />
      </button>

      <button
        type="button"
        className={styles.doneButton}
        data-component="apply-photo-edit"
        onClick={onDone}
      >
        <FormattedMessage {...dentalPhotoToolbarMessages.done} />
      </button>

      <button
        type="button"
        data-component="reset-photo-edit"
        className={styles.resetButton}
        onClick={onResetToOriginal}
      >
        {isMobile ? (
          <FormattedMessage id="global.reset" defaultMessage="Reset" />
        ) : (
          <FormattedMessage {...dentalPhotoToolbarMessages.resetToOriginal} />
        )}
      </button>
    </div>
  );

  const renderZoomControls = () => (
    <div className={styles.controls}>
      <div className={styles.iconButtons}>
        <button type="button" className={styles.iconButton} onClick={onZoomIn}>
          <Icon name="zoom" size={24} />
        </button>
        <button type="button" className={styles.iconButton} onClick={onZoomOut}>
          <Icon name="zoomOut" size={24} />
        </button>
      </div>

      {actionButtons}
    </div>
  );

  const renderCropControls = () => (
    <div className={styles.controls}>{actionButtons}</div>
  );

  const renderRotateControls = () => (
    <div className={styles.controls}>
      <div className={styles.iconButtons}>
        <button
          type="button"
          className={styles.iconButton}
          onClick={onRotateLeft}
        >
          <span className={styles.mirroredIcon}>
            <Icon name="turn" size={24} />
          </span>
        </button>
        <button
          type="button"
          className={styles.iconButton}
          onClick={onRotateRight}
        >
          <Icon name="turn" size={24} />
        </button>
      </div>

      {actionButtons}
    </div>
  );

  const renderStraightenControls = () => (
    <div className={cn(styles.controls, styles.controlsVertical)}>
      <RotationSlider
        className={styles.rotationSlider}
        value={rotation}
        onChange={onFreeRotation}
      />

      {actionButtons}
    </div>
  );

  const toolRenderers: Record<EditableToolWithControls, () => JSX.Element> = {
    zoom: renderZoomControls,
    crop: renderCropControls,
    rotate: renderRotateControls,
    straighten: renderStraightenControls,
  };

  return (
    <div className={cn(styles.container, className)}>
      {toolRenderers[activeTool]()}
    </div>
  );
};
